<template>
  <div class="sunon-ai-handle">
    <div class="sunon-ai-handle__panel">
      <PillButton
        :link="link"
        :target="target"
        :rel="resolvedRel"
        :button-color="buttonColor"
        :text-color="textColor"
        class="ai-button"
        @click="dispatchClick('button', $event)"
      >
        <span class="sunon-ai-handle__button-content">
          <svg class="sunon-ai-handle__button-icon" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path :d="buttonIconPath" fill="url(#sunon-ai-button-gradient)" />
            <defs>
              <linearGradient id="sunon-ai-button-gradient" x1="10.5001" y1="0" x2="10.5001" y2="21.0002" gradientUnits="userSpaceOnUse">
                <stop stop-color="#58BD6E" />
                <stop offset="1" stop-color="#13A7E7" />
              </linearGradient>
            </defs>
          </svg>
          <span class="handle-text">{{ content }}</span>
        </span>
      </PillButton>
    </div>

    <component
      :is="link ? 'a' : 'button'"
      class="sunon-ai-handle__trigger"
      v-bind="triggerAttributes"
      :aria-label="ariaLabel"
      @click="dispatchClick('handle', $event)"
    >
      <svg class="sunon-ai-handle__icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect width="48" height="48" rx="24" fill="white" />
        <path :d="iconPath" fill="url(#sunon-ai-handle-gradient)" />
        <defs>
          <linearGradient id="sunon-ai-handle-gradient" x1="24.0001" y1="13.4999" x2="24.0001" y2="34.5001" gradientUnits="userSpaceOnUse">
            <stop stop-color="#58BD6E" />
            <stop offset="1" stop-color="#13A7E7" />
          </linearGradient>
        </defs>
      </svg>
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import PillButton from '@/components/button.vue';

const props = withDefaults(
  defineProps<{
    content?: string;
    link?: string;
    target?: '_self' | '_blank' | '_parent' | '_top';
    rel?: string;
    buttonColor?: string;
    textColor?: string;
    ariaLabel?: string;
  }>(),
  {
    content: 'Meet Sunon AI',
    link: '/meet-sunon-ai',
    target: '_blank',
    rel: undefined,
    buttonColor: '#ffffff',
    textColor: '#222222',
    ariaLabel: 'Meet Sunon AI',
  },
);

const resolvedRel = computed(() => (
  props.rel ?? (props.target === '_blank' ? 'noopener noreferrer' : undefined)
));

const triggerAttributes = computed(() => props.link
  ? { href: props.link, target: props.target, rel: resolvedRel.value }
  : { type: 'button' });

const dispatchClick = (source: 'handle' | 'button', event: MouseEvent) => {
  window.dispatchEvent(new CustomEvent('sunon-ai-handle-click', {
    detail: { source, originalEvent: event },
  }));
};

const iconPath = 'M22.4269 14.4886C22.586 14.4792 22.7454 14.5015 22.8958 14.5541C23.0462 14.6068 23.1847 14.6889 23.3031 14.7955C23.4216 14.9022 23.5176 15.0313 23.5858 15.1754C23.6539 15.3195 23.6927 15.4757 23.7 15.6349C23.7212 15.9572 23.6161 16.2751 23.4069 16.5213C23.3044 16.6419 23.1788 16.7409 23.0376 16.8124C22.8963 16.8839 22.7423 16.9265 22.5844 16.9378C18.9286 17.1906 16.0472 20.2391 15.9081 23.999C15.7707 27.7606 18.422 31.0209 22.0506 31.5555C25.6775 32.0875 29.1136 29.7241 30.0114 26.0745C30.0866 25.7604 30.2826 25.49 30.5539 25.322C30.6885 25.239 30.8384 25.1838 30.9947 25.1598C31.151 25.1357 31.3105 25.1433 31.4639 25.182C32.1079 25.3526 32.4964 26.0185 32.3336 26.673C32.0046 28.0109 31.4035 29.2613 30.5635 30.2369L32.856 32.3763C33.1736 32.6834 33.304 33.141 33.1955 33.5733C33.0879 34.0055 32.758 34.345 32.3336 34.4623C31.9092 34.5725 31.4586 34.4378 31.162 34.1105L28.9806 31.9781C23.9257 36.0696 16.4532 33.9618 14.1432 27.7948C11.835 21.6269 16.0035 14.9288 22.4269 14.4878V14.4886ZM26.3845 21.3863C26.5437 21.3863 26.6732 21.5175 26.6732 21.6794V26.6424C26.6736 26.6806 26.6664 26.7185 26.652 26.754C26.6377 26.7894 26.6165 26.8216 26.5897 26.8489C26.5629 26.8761 26.5309 26.8978 26.4957 26.9126C26.4605 26.9275 26.4227 26.9353 26.3845 26.9355H25.8087C25.7705 26.9353 25.7327 26.9275 25.6975 26.9126C25.6623 26.8978 25.6303 26.8761 25.6035 26.8489C25.5767 26.8216 25.5555 26.7894 25.5412 26.754C25.5268 26.7185 25.5196 26.6806 25.52 26.6424V21.6794C25.52 21.5184 25.6495 21.388 25.8079 21.388H26.3854L26.3845 21.3863ZM22.0664 21.458C22.12 21.4583 22.1724 21.4735 22.2178 21.502C22.2633 21.5304 22.2999 21.5709 22.3236 21.619L24.9451 26.9355H23.5057L22.2737 24.1924C22.2554 24.152 22.2259 24.1178 22.1887 24.0937C22.1514 24.0697 22.1081 24.0568 22.0637 24.0568C22.0196 24.057 21.9764 24.0699 21.9393 24.0939C21.9022 24.118 21.8728 24.1521 21.8546 24.1924L20.6252 26.9355H19.1876L21.8082 21.619C21.8322 21.5708 21.8691 21.5303 21.9149 21.502C21.9607 21.4737 22.0134 21.4588 22.0672 21.4589V21.458H22.0664ZM29.6859 13.4999C29.8521 13.4999 30.0009 13.6066 30.0534 13.7659L30.826 16.061C31.0097 16.607 31.4332 17.0366 31.9705 17.2248L34.2332 18.007C34.3925 18.0595 34.5001 18.21 34.5001 18.3798C34.5005 18.4621 34.4749 18.5424 34.427 18.6093C34.3791 18.6763 34.3113 18.7264 34.2332 18.7525L31.9705 19.5356C31.7034 19.6292 31.4614 19.7828 31.263 19.9845C31.0646 20.1863 30.9151 20.4308 30.826 20.6994L30.0534 22.9936C30.028 23.0712 29.9788 23.1388 29.9129 23.1869C29.8469 23.2349 29.7675 23.261 29.6859 23.2614C29.6042 23.261 29.5248 23.2349 29.4588 23.1869C29.3929 23.1388 29.3437 23.0712 29.3184 22.9936L28.5457 20.6985C28.4565 20.4298 28.3067 20.1852 28.108 19.9836C27.9093 19.782 27.6669 19.6287 27.3995 19.5356L25.1394 18.7525C25.0623 18.7253 24.9957 18.6748 24.9487 18.608C24.9016 18.5412 24.8765 18.4614 24.8769 18.3798C24.8765 18.2981 24.9016 18.2183 24.9487 18.1515C24.9957 18.0847 25.0623 18.0342 25.1394 18.007L27.4004 17.2248C27.6677 17.1314 27.9101 16.9779 28.1088 16.7762C28.3076 16.5744 28.4573 16.3298 28.5466 16.061L29.3175 13.765C29.3433 13.6879 29.3926 13.6209 29.4586 13.5733C29.5245 13.5257 29.6037 13.5001 29.685 13.4999H29.6859Z';
const buttonIconPath = 'M8.92685 0.98875C9.08596 0.979311 9.24535 1.00158 9.39578 1.05426C9.54621 1.10695 9.68467 1.18899 9.80312 1.29564C9.92157 1.40229 10.0176 1.5314 10.0858 1.6755C10.1539 1.81959 10.1927 1.97578 10.2 2.135C10.2212 2.45733 10.1161 2.77525 9.90685 3.02138C9.80435 3.14202 9.67881 3.241 9.53758 3.31251C9.39634 3.38403 9.24226 3.42665 9.08435 3.43787C5.4286 3.69075 2.54723 6.73925 2.4081 10.4991C2.27073 14.2607 4.92198 17.521 8.5506 18.0556C12.1775 18.5876 15.6136 16.2242 16.5114 12.5746C16.5866 12.2605 16.7826 11.9901 17.0539 11.8221C17.1885 11.7391 17.3384 11.6839 17.4947 11.6599C17.651 11.6358 17.8105 11.6434 17.9639 11.6821C18.6079 11.8527 18.9964 12.5186 18.8336 13.1731C18.5046 14.511 17.9035 15.7614 17.0635 16.737L19.356 18.8764C19.6736 19.1835 19.804 19.6411 19.6955 20.0734C19.5879 20.5056 19.258 20.8451 18.8336 20.9624C18.4092 21.0726 17.9586 20.9379 17.662 20.6106L15.4806 18.4783C10.4257 22.5697 2.95323 20.4619 0.643228 14.2949C-1.66502 8.127 2.50348 1.42887 8.92685 0.987875V0.98875ZM12.8845 7.88637C13.0437 7.88637 13.1732 8.01762 13.1732 8.1795V13.1425C13.1736 13.1807 13.1664 13.2186 13.152 13.2541C13.1377 13.2895 13.1165 13.3218 13.0897 13.349C13.0629 13.3762 13.0309 13.3979 12.9957 13.4128C12.9605 13.4276 12.9227 13.4354 12.8845 13.4356H12.3087C12.2705 13.4354 12.2327 13.4276 12.1975 13.4128C12.1623 13.3979 12.1303 13.3762 12.1035 13.349C12.0767 13.3218 12.0555 13.2895 12.0412 13.2541C12.0268 13.2186 12.0196 13.1807 12.02 13.1425V8.1795C12.02 8.0185 12.1495 7.88813 12.3079 7.88813H12.8854L12.8845 7.88637ZM8.56635 7.95813C8.61995 7.95844 8.6724 7.97367 8.71783 8.0021C8.76327 8.03054 8.79989 8.07106 8.8236 8.11912L11.4451 13.4356H10.0057L8.77373 10.6925C8.75543 10.6521 8.7259 10.6179 8.68866 10.5938C8.65142 10.5698 8.60806 10.5569 8.56373 10.5569C8.51955 10.5571 8.47637 10.57 8.4393 10.5941C8.40224 10.6181 8.37284 10.6523 8.3546 10.6925L7.12523 13.4356H5.6876L8.30823 8.11912C8.33219 8.07096 8.36914 8.03044 8.41491 8.00215C8.46067 7.97386 8.51343 7.95891 8.56723 7.959V7.95813H8.56635ZM16.1859 0C16.3521 0 16.5009 0.10675 16.5534 0.266L17.326 2.56113C17.5097 3.10713 17.9332 3.53675 18.4705 3.72487L20.7332 4.50712C20.8925 4.55963 21.0001 4.71012 21.0001 4.87987C21.0005 4.96218 20.9749 5.04252 20.927 5.10945C20.8791 5.17637 20.8113 5.22648 20.7332 5.25262L18.4705 6.03575C18.2034 6.12932 17.9614 6.28288 17.763 6.48463C17.5646 6.68638 17.4151 6.93093 17.326 7.1995L16.5534 9.49375C16.528 9.57133 16.4788 9.63894 16.4129 9.687C16.3469 9.73507 16.2675 9.76113 16.1859 9.7615C16.1042 9.76113 16.0248 9.73507 15.9588 9.687C15.8929 9.63894 15.8437 9.57133 15.8184 9.49375L15.0457 7.19863C14.9565 6.92994 14.8067 6.68535 14.608 6.48372C14.4093 6.2821 14.1669 6.12883 13.8995 6.03575L11.6394 5.25262C11.5623 5.2254 11.4957 5.17489 11.4487 5.10811C11.4016 5.04132 11.3765 4.96156 11.3769 4.87987C11.3765 4.79819 11.4016 4.71843 11.4487 4.65164C11.4957 4.58486 11.5623 4.53435 11.6394 4.50712L13.9004 3.72487C14.1677 3.63153 14.4101 3.47807 14.6088 3.27631C14.8076 3.07455 14.9573 2.82988 15.0466 2.56113L15.8175 0.265125C15.8433 0.188039 15.8926 0.120994 15.9586 0.0734315C16.0245 0.0258691 16.1037 0.00018621 16.185 0L16.1859 0Z';
</script>

<style scoped lang="scss">
:global([data-sunon-ai-handle-mounted='true']) {
  overflow: visible !important;
}
.sunon-ai-handle {
  position: relative;
  z-index: 20;
  display: inline-flex;
  width: 100%;
  height: 100%;
  overflow: visible;
  vertical-align: middle;

  &::before {
    position: absolute;
    top: 0;
    right: 100%;
    bottom: 0;
    width: 12px;
    content: '';
  }
  .ai-button{
    padding-top: 0;
    padding-bottom: 0;
    filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.15));
  }
}

.sunon-ai-handle__trigger {
  position: relative;
  z-index: 2;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  overflow: hidden;
  appearance: none;
  cursor: pointer;
  background: #fff;
  border: 0;
  border-radius: 50%;
  box-shadow: 0 6px 16px rgba(0, 0, 0, .12);
  transition: box-shadow 220ms ease, transform 220ms ease;

  &:hover { box-shadow: 0 8px 22px rgba(0, 0, 0, .18); transform: translateY(-1px); }
  &:focus-visible { outline: 2px solid #13a7e7; outline-offset: 3px; }
}

.sunon-ai-handle__icon { display: block; width: 100%; height: 100%; }

.sunon-ai-handle__panel {
  position: absolute;
  top: 0;
  right: calc(100% + 10px);
  display: flex;
  align-items: center;
  height: 100%;
  pointer-events: none;
  visibility: hidden;
  transform: translateX(14px) scaleX(.92);
  transform-origin: right center;
  transition: transform 220ms ease, visibility 0s linear 220ms;
}

.sunon-ai-handle__button-content {
  display: flex;
  align-items: center;
  gap: calc(var(--sunon-ai-handle-size, 40px) / 4);
  white-space: nowrap;
  height: calc(var(--sunon-ai-handle-size, 40px));
  padding-right: calc(var(--sunon-ai-handle-size, 40px) / 2);
}

.sunon-ai-handle__button-icon {
  flex: 0 0 auto;
  width: calc(var(--sunon-ai-handle-size, 40px) * .4375);
  height: calc(var(--sunon-ai-handle-size, 40px) * .4375);
}

.sunon-ai-handle:hover .sunon-ai-handle__panel,
.sunon-ai-handle:focus-within .sunon-ai-handle__panel {
  pointer-events: auto;
  visibility: visible;
  transform: translateX(0) scaleX(1);
  transition-delay: 0s;
}

@media (prefers-reduced-motion: reduce) {
  .sunon-ai-handle__trigger,
  .sunon-ai-handle__panel { transition: none; }
}
</style>
